import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDown, Loader2, Search } from 'lucide-react';
import { useGlobalCountries } from '@/hooks/useGlobalCountries';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';

interface CountrySelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const CountrySelect: React.FC<CountrySelectProps> = ({
  value,
  onChange,
  placeholder = "Selecione o país",
  disabled = false,
  className = ''
}) => {
  const { data: countries = [], isLoading, error } = useGlobalCountries();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const selectedCountry = useMemo(() => 
    countries.find(c => c.code === value),
    [countries, value]
  );

  const filteredCountries = useMemo(() => {
    if (!searchTerm) return countries;
    
    const term = searchTerm.toLowerCase();
    return countries.filter(c => 
      c.name.toLowerCase().includes(term) || 
      c.code.toLowerCase().includes(term)
    );
  }, [countries, searchTerm]);

  const handleSelect = (code: string) => {
    onChange(code);
    setIsOpen(false);
    setSearchTerm('');
  };

  const displayValue = selectedCountry 
    ? `${selectedCountry.flag} ${selectedCountry.name}` 
    : placeholder;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={isOpen}
        disabled={disabled || isLoading}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full justify-between bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white focus:border-luxury-pinkBright/50"
      >
        <span className={!selectedCountry ? 'text-white/40' : ''}>
          {isLoading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando...
            </span>
          ) : error ? (
            'Erro ao carregar países'
          ) : (
            displayValue
          )}
        </span>
        <ChevronDown className={`ml-2 h-4 w-4 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && !isLoading && !error && (
        <Card className="absolute z-50 w-full mt-1 bg-gray-900 border-white/20 shadow-xl max-h-[300px] overflow-hidden">
          <div className="p-2 border-b border-white/10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <Input
                ref={inputRef}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar país..."
                className="pl-9 bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-luxury-pinkBright/50"
              />
            </div>
          </div>
          
          <div className="overflow-y-auto max-h-[240px] p-1">
            {filteredCountries.length === 0 ? (
              <div className="p-3 text-center text-white/50 text-sm">
                Nenhum país encontrado
              </div>
            ) : (
              filteredCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleSelect(country.code)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                    country.code === value 
                      ? 'bg-luxury-pinkBright/20 text-white' 
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <span className="text-lg">{country.flag}</span>
                  <span className="flex-1">{country.name}</span>
                  <span className="text-white/40 text-xs">{country.code}</span>
                </button>
              ))
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default CountrySelect;
